import { createElement, type ComponentType, type SVGProps } from "react";
import type { LucideIcon } from "lucide-react";
import {
  BadgeDollarSign,
  ClipboardList,
  Factory,
  LayoutGrid,
  Package2,
  Settings2,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Workflow,
  Wrench,
  UsersRound,
} from "lucide-react";

export type IconeComponente = ComponentType<SVGProps<SVGSVGElement>>;

function criarIconeMenu(Icone: LucideIcon): IconeComponente {
  return function IconeMenu(props: SVGProps<SVGSVGElement>) {
    return createElement(Icone, { strokeWidth: 1.9, ...props });
  };
}

const IconeMenuVisaoGeral = criarIconeMenu(LayoutGrid);
const IconeMenuCadastros = criarIconeMenu(ClipboardList);
const IconeMenuVendas = criarIconeMenu(ShoppingCart);
const IconeMenuCompras = criarIconeMenu(ShoppingBag);
const IconeMenuFornecedor = criarIconeMenu(Factory);
const IconeMenuEstoque = criarIconeMenu(Package2);
const IconeMenuFinanceiro = criarIconeMenu(BadgeDollarSign);
const IconeMenuSuporte = criarIconeMenu(Wrench);
const IconeMenuProducao = criarIconeMenu(Workflow);
const IconeMenuAreaCliente = criarIconeMenu(UsersRound);
export const IconeMenuPlataforma = criarIconeMenu(Settings2);
export const IconeMenuAuditoria = criarIconeMenu(ShieldCheck);

export interface SubItemMenu {
  rotulo: string;
  rota: string;
  modulo?: string | string[];
  /** Navega sem substituir a tela atual, usado por áreas de trabalho independentes. */
  novaAba?: boolean;
}

export interface ItemMenu {
  rotulo: string;
  Icone: IconeComponente;
  rota?: string;
  modulo?: string | string[];
  subitens?: SubItemMenu[];
}

export const menu: ItemMenu[] = [
  { rotulo: "Visão Geral", rota: "/", Icone: IconeMenuVisaoGeral },
  {
    rotulo: "Cadastros",
    Icone: IconeMenuCadastros,
    modulo: "b27",
    subitens: [
      { rotulo: "Empresa", rota: "/cadastros/empresas" },
      { rotulo: "Banco", rota: "/cadastros/bancos" },
      { rotulo: "Clientes", rota: "/cadastros/clientes" },
      { rotulo: "Fornecedor", rota: "/cadastros/fornecedores" },
      { rotulo: "Transportadora", rota: "/cadastros/transportadoras" },
      { rotulo: "Colaborador", rota: "/cadastros/colaboradores" },
      { rotulo: "Produto", rota: "/cadastros/produtos" },
      { rotulo: "Faixas de Preço", rota: "/cadastros/faixas-preco" },
      { rotulo: "Tabela de preço", rota: "/cadastros/detalhes" },
      { rotulo: "Tabela de detalhes", rota: "/cadastros/tabela-detalhes" },
      { rotulo: "Serviço", rota: "/cadastros/servicos" },
      { rotulo: "Unidade de Medida", rota: "/cadastros/unidades-medida" },
      { rotulo: "Categoria", rota: "/cadastros/categorias-estoque" },
      { rotulo: "Detalhes da Ficha", rota: "/cadastros/detalhes-ficha" },
      { rotulo: "Etapas", rota: "/cadastros/etapas" },
      { rotulo: "Oficinas", rota: "/cadastros/oficinas" },
      { rotulo: "Armazenamento", rota: "/cadastros/locais-estoque" },
      { rotulo: "Tamanho", rota: "/cadastros/tamanhos" },
      { rotulo: "Cor", rota: "/cadastros/cores" },
      { rotulo: "Etiqueta", rota: "/cadastros/etiquetas" },
      { rotulo: "Respostas Rápidas", rota: "/cadastros/respostas-rapidas" },
      { rotulo: "Plano de Contas", rota: "/financeiro/plano-contas", modulo: "b10" },
      { rotulo: "Maquininhas e Taxas", rota: "/financeiro/maquininhas", modulo: "b10" },
      { rotulo: "Erro", rota: "/cadastros/erros" },
      { rotulo: "Serial", rota: "/cadastros/seriais" },
      { rotulo: "Metas", rota: "/cadastros/metas" },
      { rotulo: "Modelos de Checklist", rota: "/cadastros/checklists" },
      { rotulo: "Perfil de Usuário", rota: "/usuarios/perfis" },
      { rotulo: "Login", rota: "/usuarios/logins" },
    ],
  },
  {
    rotulo: "Vendas",
    Icone: IconeMenuVendas,
    modulo: "b13",
    subitens: [
      { rotulo: "Vendas", rota: "/vendas", modulo: "b13" },
      { rotulo: "Cobranças", rota: "/vendas/cobrancas", modulo: "b13" },
      { rotulo: "Ficha técnica", rota: "/vendas/ficha-tecnica", modulo: "b13", novaAba: true },
      { rotulo: "Entregas", rota: "/vendas/entregas", modulo: "b13" },
      { rotulo: "Atendimento", rota: "/vendas/atendimento", modulo: "b13" },
      { rotulo: "Monitor Fiscal", rota: "/vendas/monitor-fiscal", modulo: "b13" },
      { rotulo: "NF-e Recebidas", rota: "/vendas/nfes-recebidas", modulo: "b13" },
      { rotulo: "Avaliações de Vendas", rota: "/vendas/avaliacoes-atendimento", modulo: "b13" },
      { rotulo: "Relatórios", rota: "/vendas/relatorios", modulo: "b13" },
    ],
  },
  {
    rotulo: "Compras",
    Icone: IconeMenuCompras,
    modulo: "b11",
    // Cotações foi descontinuada; este menu mantém somente as telas ativas de Compras.
    subitens: [
      { rotulo: "Pedido de Compra", rota: "/compras", modulo: "b11" },
      { rotulo: "Saldo Interno", rota: "/compras/saldo-compras", modulo: "b11" },
      // O catálogo do Perfil de Acesso deriva deste menu e mantém a permissão própria de enviados.
      { rotulo: "Comprovantes de pagamentos", rota: "/compras/comprovantes-pagamentos", modulo: "b11" },
      { rotulo: "Gráfico de Gantt", rota: "/compras/gantt", modulo: "b11" },
      { rotulo: "Relatórios", rota: "/compras/relatorios", modulo: "b11" },
    ],
  },
  {
    // Revenda é o nome comercial do módulo /fornecedor no menu.
    rotulo: "Revenda",
    Icone: IconeMenuFornecedor,
    modulo: "b11",
    subitens: [
      { rotulo: "Dashboard", rota: "/fornecedor/visao-geral", modulo: "b11" },
      { rotulo: "Pedido de Revenda", rota: "/fornecedor/pedidos", modulo: "b11" },
      { rotulo: "Saldo Interno", rota: "/fornecedor/saldo-interno", modulo: "b11" },
      { rotulo: "Cobranças", rota: "/fornecedor/cobrancas", modulo: "b11" },
      { rotulo: "Entregas", rota: "/fornecedor/entregas", modulo: "b11" },
      { rotulo: "Envio de Nota Fiscal", rota: "/fornecedor/envio-nota-fiscal", modulo: "b11" },
      { rotulo: "Distribuição", rota: "/fornecedor/distribuicao", modulo: "b11" },
      { rotulo: "Comprovantes", rota: "/fornecedor/comprovantes-pagamentos", modulo: "b11" },
      { rotulo: "Relatórios", rota: "/fornecedor/relatorios", modulo: "b11" },
    ],
  },
  {
    rotulo: "Estoque",
    Icone: IconeMenuEstoque,
    modulo: "b17",
    subitens: [
      { rotulo: "Saldo Atual", rota: "/estoque/saldo" },
      { rotulo: "Movimentações", rota: "/estoque/movimentacoes" },
      { rotulo: "Transferências", rota: "/estoque/transferencias" },
      { rotulo: "Inventário", rota: "/estoque/inventario-ajuste-estoque" },
      { rotulo: "Relatórios", rota: "/estoque/relatorios" },
    ],
  },
  {
    rotulo: "Financeiro",
    Icone: IconeMenuFinanceiro,
    modulo: "b10",
    subitens: [
      { rotulo: "Contas a Receber", rota: "/financeiro/receitas" },
      { rotulo: "Vendas Parceladas", rota: "/financeiro/vendas-parceladas" },
      { rotulo: "Contas a Pagar", rota: "/financeiro/despesas" },
      { rotulo: "Comissões", rota: "/financeiro/comissoes" },
      { rotulo: "Fluxo de Caixa", rota: "/financeiro/fluxo-caixa-diario-mensal" },
      { rotulo: "DRE", rota: "/financeiro/dre-gerencial-comercial" },
      { rotulo: "Previsibilidade", rota: "/financeiro/previsibilidade" },
      { rotulo: "Relatórios", rota: "/financeiro/relatorios" },
    ],
  },
  {
    rotulo: "Tickets",
    Icone: IconeMenuSuporte,
    modulo: "b12",
    subitens: [
      { rotulo: "Chamados", rota: "/suporte" },
      { rotulo: "Ativações", rota: "/suporte/ativacoes" },
      { rotulo: "Kanban", rota: "/suporte/kanban" },
      { rotulo: "Calendário", rota: "/suporte/calendario" },
      { rotulo: "Atendimento de Tickets", rota: "/suporte/chat" },
      { rotulo: "Checklist", rota: "/suporte/checklists" },
      { rotulo: "Suporte Externo", rota: "/suporte/externo" },
      { rotulo: "Orçamento de Suporte", rota: "/suporte/orcamentos" },
      { rotulo: "Avaliações de Tickets", rota: "/suporte/avaliacoes-atendimento" },
      { rotulo: "Relatórios", rota: "/suporte/relatorios" },
    ],
  },
  {
    /* Módulo próprio, logo abaixo de Tickets. Usa o mesmo código de módulo do
       suporte (b12) porque as telas nasceram desse fluxo — assim o grupo só
       aparece para quem tem o módulo de Tickets ativo. */
    rotulo: "Produção",
    Icone: IconeMenuProducao,
    modulo: "b12",
    subitens: [
      { rotulo: "Formatação", rota: "/producao/formatacao" },
      { rotulo: "Lista de produção", rota: "/producao/lista" },
      { rotulo: "Kanban de formatação", rota: "/producao/kanban" },
      { rotulo: "Kanban de produção", rota: "/producao/controle" },
      { rotulo: "Calendário de produção", rota: "/producao/calendario" },
      { rotulo: "Gráfico de Gantt", rota: "/producao/gantt" },
      { rotulo: "Gestão de Oficinas", rota: "/producao/oficinas" },
      { rotulo: "Produção de Estoque", rota: "/producao/estoque" },
      { rotulo: "Impressões DTF", rota: "/producao/impressoes-dtf" },
      { rotulo: "Vendas de DTF", rota: "/producao/vendas-dtf" },
      { rotulo: "Comprovantes DTF", rota: "/producao/comprovantes-dtf" },
      { rotulo: "Avaria / Defeito e erros", rota: "/producao/avarias" },
      { rotulo: "Comissões", rota: "/producao/comissoes" },
      { rotulo: "Expedição", rota: "/producao/expedicao" },
      { rotulo: "Checklist de produção", rota: "/producao/checklists" },
      { rotulo: "Avaliações de Produção", rota: "/producao/avaliacoes-atendimento" },
      { rotulo: "Relatórios", rota: "/producao/relatorios" },
    ],
  },
  {
    rotulo: "Área do Cliente",
    Icone: IconeMenuAreaCliente,
    modulo: ["b12", "b13"],
    subitens: [
      { rotulo: "Pedido de Cliente", rota: "/area-cliente/pedidos", modulo: "b13" },
      { rotulo: "Chamados", rota: "/area-cliente/chamados", modulo: "b12" },
      { rotulo: "Chat", rota: "/area-cliente/chat" },
      { rotulo: "Relatórios", rota: "/area-cliente/relatorios", modulo: ["b12", "b13"] },
    ],
  },
];
